import React from 'react';
import { toast } from 'react-toastify';
import {
  IOrder,
  AddToShoppingCartSource,
  INoteAttribute,
} from '@monofront/types';
import { ModalConfirmation } from '@components/ModalConfirmation';
import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import { dataLayerTrackEvent, trackShoppingCartUpdate } from '@utils/tracking';
import { trackEvent } from '@monofront/tracking';
import logger from '@utils/logger';
import { useTranslations } from 'next-intl';
import useI18n from '@hooks/i18n/useI18n';
import {
  formatSearchProductToTracking,
  getIsFresh,
} from '@monofront/business/utils';
import { productService } from '@services/businessContainer';
import {
  EVENT_SOURCE,
  FEATURE_FLAG_FRESH_ELIGIBILITY,
} from '@monofront/business/constants';
import { useGrowthBook } from '@monofront/business/contexts';
import { isMobileApp } from '@utils/environment';

interface IReorderConfirmationPopupProps {
  order: IOrder;
  isOpen: boolean;
  hide: () => void;
}

export const ReorderModalConfirmation: React.FC<
  IReorderConfirmationPopupProps
> = ({ order, isOpen, hide }) => {
  const { channel, currency } = useI18n();
  const tCart = useTranslations('Cart');
  const growthbook = useGrowthBook();
  const tCatalog = useTranslations('Catalog');
  const tErrors = useTranslations('Errors');
  const {
    shoppingCartId,
    addProductsToShoppingCart,
    addProductsToShoppingCartBySku,
  } = useShoppingCart();

  const { name } = order;

  const reorder = async () => {
    try {
      if (!shoppingCartId) {
        throw new Error('Unexpected empty `shoppingCartId`');
      }

      let products = order.lineItems;

      const isFreshOrder =
        order.noteAttributes.find(
          (noteAttribute: INoteAttribute) => noteAttribute.name === 'isFresh',
        )?.value === 'true';

      const isFreshEligible = growthbook?.isOn(FEATURE_FLAG_FRESH_ELIGIBILITY);

      // Do not add fresh products to cart if user is not eligible anymore
      if (isFreshOrder && !isFreshEligible) {
        const algoliaProducts = await productService.getProductsBySku(
          products.map(({ sku }) => sku),
          channel,
        );

        const nonFreshSkus = algoliaProducts.reduce(
          (filtered: string[], product) => {
            if (!getIsFresh(product.tags)) {
              filtered.push(product.sku);
            }

            return filtered;
          },
          [],
        );

        products = products.filter((product) =>
          nonFreshSkus.includes(product.sku),
        );
      }

      if (order.sourceName === 'oms') {
        await addProductsToShoppingCartBySku(shoppingCartId, products, channel);
      } else {
        await addProductsToShoppingCart(shoppingCartId, products);
      }

      isFreshOrder && !isFreshEligible
        ? toast(tCart('productsNotFreshAddedToCart'))
        : toast(tCart('productsAddedToCart'));

      // deprecated tracker https://gitlab.com/la-fourche/scrum/-/issues/1968
      trackShoppingCartUpdate('addToCart', products, undefined, undefined);
      trackEvent({
        type: 'LFAddToCart',
        data: {
          products: products.map((product) =>
            formatSearchProductToTracking(product, product.quantity),
          ),
          context: { source: AddToShoppingCartSource.DEFAULT },
          currency,
          is_substitute: false,
          eventSource: isMobileApp ? EVENT_SOURCE.APP : EVENT_SOURCE.WEB,
        },
      });

      // deprecated tracker https://gitlab.com/la-fourche/scrum/-/issues/1968
      dataLayerTrackEvent('ReorderWidget', {
        action: 'reorderModalConfirmation',
      });

      trackEvent({ type: 'LFReorderWidget', data: { action: 'validate' } });
    } catch (error: any) {
      toast(tErrors('addProductsToCartError'), {
        progressClassName: 'toast-error',
      });

      logger.error(error);
    } finally {
      hide();
    }
  };

  return (
    <>
      <ModalConfirmation
        title={tCatalog('replaceOrder', { orderName: name })}
        message={tCatalog('replaceOrderConfirmation', { orderName: name })}
        isOpen={isOpen}
        confirmButtonLabel={tCart('addToCartConfirm')}
        confirmButtonOnClick={async () => {
          await reorder();
        }}
        cancelButtonOnClick={hide}
        modalClassName="reorder-order-modal"
      />
      <style jsx>{`
        :global(.reorder-order-modal) {
          top: 33%;
          width: 100%;
          max-width: 560px;
        }
        @media (max-width: 800px) {
          :global(.reorder-order-modal .modal-cta > button:last-child) {
            width: 180px;
          }
          :global(.reorder-order-modal .modal-cta > button:first-child) {
            margin-right: var(--space-1);
          }
        }
        @media (max-width: 360px) {
          :global(.reorder-order-modal .modal-cta > button) {
            margin: 0;
          }
        }
      `}</style>
    </>
  );
};
